import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { ReminderSettings } from '../components/ReminderSettings';
import { supabase } from '../lib/supabase';
import { ProtectedLayout } from '../components/ProtectedLayout';

export function CoreValueConfirmation() {
  const navigate = useNavigate();
  const [coreValues, setCoreValues] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCoreValues = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate('/');
          return;
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('core_value_1, core_value_2, core_value_3')
          .eq('id', user.id)
          .single();

        if (profile) {
          setCoreValues(
            [profile.core_value_1, profile.core_value_2, profile.core_value_3].filter(Boolean)
          );
        }
      } catch (error) {
        console.error('Error fetching core values:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCoreValues();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex items-center justify-center">
        <div className="text-[#236caf] text-lg font-medium">Loading...</div>
      </div>
    );
  }

  return (
    <ProtectedLayout>
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-3xl w-full bg-white rounded-xl shadow-lg p-8 space-y-8">
        <h1 className="text-3xl font-bold text-[#004060]">
          Congratulations. You've just taken your first step toward meaningful growth.
        </h1>

        <div className="space-y-4">
          <p className="text-xl text-gray-700">Your core values are:</p>

          <div className="flex flex-wrap gap-3">
            {coreValues.map((value, index) => (
              <div
                key={index}
                className="px-4 py-2 bg-blue-50 text-[#236caf] font-medium rounded-full"
              >
                {value}
              </div>
            ))}
          </div>

          <p className="text-gray-700">
            These values can shape how you lead, connect, create, and solve challenges.
            But values alone aren't enough—we need a system to live them. That's the power of this app.
          </p>
        </div>

        <div className="space-y-4">
          <p className="text-gray-700">
            Next, set up your reminders for morning journaling and afternoon celebration.
            These are optional, but especially early on, they are helpful for making use of your fears.
            You can update these settings anytime in your profile.
          </p>

          <div className="bg-gradient-to-r from-[#004060] to-[#236caf] text-white p-6 rounded-xl space-y-4">
            <p className="text-xl font-medium">Reminder Settings</p>
            <div className="bg-white/10 p-6 rounded-lg">
              <ReminderSettings defaultUseText={false} />
            </div>
          </div>
           <p className="text-gray-700">
            That's it for the onboarding. The dashboard has a Getting Started section with helpful guidance on how the Alignment Engine and Planner work together to help you create clarity and take aligned action. Nothing takes more than 5 minutes at a time. Journal, act, and reflect. Repeat those steps and you'll learn how to use your fears for good. Enjoy.
          </p>
        </div>

        <div className="flex gap-4 pt-6">
          <Button
            variant="outline"
            onClick={() => navigate('/core-values')}
            className="w-1/2"
          >
            Back
          </Button>

          <Button
            onClick={() => navigate('/dashgrowthloop')}
            className="w-1/2"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
    </ProtectedLayout>
  );
}